<template>
  <div>
    <FormulateForm
      ref="form"
      name="formDeliveryConfig"
    >
      <!-- Passo 1 — Escopo (definido na listagem): mostrado apenas como contexto, não editável. -->
      <b-card-actions
        no-actions
        :show-loading="loading || busy"
      >
        <div class="d-flex align-items-center">
          <feather-icon
            :icon="scope === 'general' ? 'GlobeIcon' : 'HomeIcon'"
            size="22"
            class="mr-1 text-primary"
          />
          <div>
            <h5 class="mb-0">
              {{ scopeTitle }}
            </h5>
            <small class="text-muted">{{ scopeSubtitle }}</small>
          </div>
        </div>

        <!-- Seleção de loja apenas ao criar uma configuração nova (loja ainda não escolhida). -->
        <b-row
          v-if="scope === 'store' && !isStoreLocked"
          class="mt-1"
        >
          <b-col md="4">
            <FormulateInput
              id="delivery-config-store"
              v-model="storeModel"
              type="vue-select"
              name="storeId"
              :label="$t('Loja')"
              :options="storeOptions"
              :placeholder="$t('Selecione a loja')"
            />
          </b-col>
        </b-row>

        <!-- Status da loja: deixa explícito se ela tem config própria ou usa a Geral. -->
        <b-alert
          v-if="scope === 'store' && selectedStoreId && hasOwnConfig"
          variant="success"
          show
          class="mt-1 mb-0"
        >
          <div class="alert-body d-flex align-items-center justify-content-between flex-wrap">
            <span>
              <feather-icon icon="CheckCircleIcon" />
              {{ $t('Esta loja tem configuração PRÓPRIA de delivery.') }}
            </span>
            <e-button
              variant="flat-danger"
              icon="trash-2"
              :text="$t('Excluir e voltar a usar a Geral')"
              @click="removeOwnConfig"
            />
          </div>
        </b-alert>
        <b-alert
          v-else-if="scope === 'store' && selectedStoreId && !hasOwnConfig"
          variant="info"
          show
          class="mt-1 mb-0"
        >
          <div class="alert-body">
            <feather-icon icon="InfoIcon" />
            {{ $t('Esta loja usa a configuração GERAL. Preencha os campos abaixo e salve para criar uma configuração própria.') }}
          </div>
        </b-alert>
      </b-card-actions>

      <!-- Passo 2 — Edição da configuração BASE (padrão) do escopo. Campanhas ficam em tela própria. -->
      <b-card-actions
        v-if="canEdit"
        no-actions
        :show-loading="loading || busy"
      >
        <p class="text-muted mb-1">
          {{ editingHint }}
        </p>
        <b-tabs>
          <b-tab
            :title="$t('Configuração base')"
            active
          >
            <delivery-config-defaults :config="config" />
          </b-tab>
          <b-tab :title="$t('Ajustes por dia da semana')">
            <delivery-config-by-day :config="config" />
          </b-tab>
        </b-tabs>
      </b-card-actions>

      <b-alert
        v-else
        variant="info"
        show
        class="mt-1"
      >
        <div class="alert-body">
          {{ $t('Selecione uma loja para editar a configuração de delivery.') }}
        </div>
      </b-alert>

      <FAB
        :main-tooltip="$t('Ações')"
        main-icon="keyboard_command_key"
        :actions="actions"
        :fixed-tooltip="true"
        :busy="busy"
        @save="save"
        @cancel="cancel"
      />
    </FormulateForm>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { BRow, BCol, BTabs, BTab, BAlert } from 'bootstrap-vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { alerts } from '@/mixins'
import { EButton, FAB } from '@/views/components'
import DeliveryConfigDefaults from '../components/DeliveryConfigDefaults.vue'
import DeliveryConfigByDay from '../components/DeliveryConfigByDay.vue'

/**
 * Tela dedicada de "Configuração de Delivery" (D28/D29, tarefa 16.1), fora do `StoreMaintain` e
 * acessível pelo menu "Lojas".
 *
 * Permite selecionar o escopo — a config Geral (`StoreId` nulo, D29) ou uma loja específica — e
 * editar a `StoreDeliveryConfig` daquele escopo (config base + campanhas), hospedando os blocos
 * `DeliveryConfigDefaults` (padrão + faixas padrão) e `DeliveryConfigByDay` (grade dos sete dias).
 * Consome `GET/PUT /stores/{id}/delivery-config` (loja) e `GET/PUT /store-delivery-config/general`
 * (Geral) pelo módulo Vuex `delivery-config.js`. Não há replicação (removida — D29).
 */
export default {
  components: {
    BRow,
    BCol,
    BTabs,
    BTab,
    BAlert,
    BCardActions,
    EButton,
    FAB,
    DeliveryConfigDefaults,
    DeliveryConfigByDay,
  },

  mixins: [alerts],

  data() {
    return {
      loading: false,
      busy: false,
      // Loja veio fixada pela listagem (edição): trava o escopo (sem seletor de loja).
      storeLocked: false,
    }
  },

  computed: {
    ...mapState('pages/store/deliveryConfig', ['scope', 'selectedStoreId', 'config', 'configs']),
    ...mapGetters('pages/store/deliveryConfig', ['storeOptions', 'baseConfig', 'hasOwnConfig']),

    // Texto contextual que explica exatamente O QUE está sendo editado agora (sempre a base).
    editingHint() {
      if (this.scope === 'general') {
        return this.$t('Você está editando a configuração GERAL, usada por todas as lojas sem configuração própria.')
      }
      if (!this.hasOwnConfig) {
        return this.$t('Você está criando a configuração própria desta loja. Enquanto não salvar, ela continua usando a Geral.')
      }
      return this.$t('Você está editando a configuração própria desta loja.')
    },

    // A loja fica travada (escopo definido pela listagem) quando veio um storeId na navegação.
    isStoreLocked() {
      return this.storeLocked
    },

    // Nome da loja selecionada (para o cabeçalho de contexto).
    selectedStoreName() {
      const found = this.storeOptions.find(option => option.value === this.selectedStoreId)
      return found ? found.label : null
    },

    // Título do cabeçalho de contexto (não editável): identifica o escopo em edição.
    scopeTitle() {
      if (this.scope === 'general') {
        return this.$t('Configuração Geral (todas as lojas)')
      }
      if (this.selectedStoreId) {
        return this.selectedStoreName || this.$t('Configuração da loja')
      }
      return this.$t('Nova configuração de loja')
    },

    // Subtítulo explicativo do escopo.
    scopeSubtitle() {
      if (this.scope === 'general') {
        return this.$t('Vale para todas as lojas que não têm configuração própria.')
      }
      if (this.selectedStoreId) {
        return this.$t('Configuração de delivery específica desta loja.')
      }
      return this.$t('Selecione a loja abaixo para começar a configurar.')
    },

    storeModel: {
      get() {
        return this.selectedStoreId
      },
      set(value) {
        this.setSelectedStore(value)
        this.onScopeOrStoreChange()
      },
    },

    // Habilita o editor: Geral sempre; loja só depois de selecionada.
    canEdit() {
      return this.scope === 'general' || !!this.selectedStoreId
    },

    actions() {
      return [
        {
          name: 'cancel',
          icon: 'arrow_back',
          color: 'red',
          tooltip: this.$t('Voltar'),
        },
        {
          name: 'save',
          icon: 'save',
          tooltip: this.$t('Salvar'),
        },
      ]
    },
  },

  async mounted() {
    this.loading = true
    try {
      await this.fetchStores()
      await this.fetchGroups()

      // Escopo/loja vêm da tela de listagem via query (?scope=general|store&storeId=123).
      const { scope, storeId } = this.$route.query
      if (scope === 'general' || scope === 'store') {
        this.setScope(scope)
      }
      // Com storeId edita a loja informada e trava o escopo; sem ele (fluxo "nova configuração de
      // loja"), zera a seleção para o usuário escolher a loja em vez de reaproveitar a do estado.
      this.storeLocked = !!storeId
      this.setSelectedStore(storeId ? Number(storeId) : null)

      await this.onScopeOrStoreChange()
    } catch (error) {
      this.showGenericError({ error })
    } finally {
      this.loading = false
    }
  },

  beforeDestroy() {
    this.clearConfig()
  },

  methods: {
    ...mapActions('pages/store/deliveryConfig', [
      'setScope',
      'setSelectedStore',
      'fetchStores',
      'fetchGroups',
      'fetchDeliveryConfig',
      'fetchGeneralDeliveryConfig',
      'saveDeliveryConfig',
      'saveGeneralDeliveryConfig',
      'deleteOwnConfig',
      'selectConfig',
      'clearConfig',
    ]),

    // Exclui a configuração própria da loja (base + campanhas) para voltar a usar a Geral (D29).
    async removeOwnConfig() {
      const confirmed = await this.confirm({
        title: this.$t('Excluir configuração da loja?'),
        text: this.$t('A loja voltará a usar a configuração Geral. Esta ação não pode ser desfeita.'),
        confirmButtonText: this.$t('Sim, excluir'),
      })
      if (!confirmed) return

      try {
        this.busy = true
        await this.deleteOwnConfig({ storeId: this.selectedStoreId })
        this.showSuccess({ message: this.$t('Configuração da loja excluída. A loja agora usa a Geral.') })
        await this.onScopeOrStoreChange()
      } catch (error) {
        this.showError({ error, message: this.$t('Não foi possível excluir a configuração da loja') })
      } finally {
        this.busy = false
      }
    },

    // Recarrega as configs do escopo/loja atuais e seleciona a config BASE para edição.
    async onScopeOrStoreChange() {
      if (!this.canEdit) {
        this.selectConfig(null)
        return
      }
      this.loading = true
      try {
        if (this.scope === 'store') {
          await this.fetchDeliveryConfig({ storeId: this.selectedStoreId })
        } else {
          await this.fetchGeneralDeliveryConfig()
        }
        this.selectConfig(this.baseConfig ? this.baseConfig.id : null)
      } catch (error) {
        this.showError({ error, message: this.$t('Não foi possível carregar a configuração') })
      } finally {
        this.loading = false
      }
    },

    // I7: a janela de entrega de cada dia exige "Abre" e "Fecha" ambos preenchidos ou ambos vazios
    // (vazio = herda o horário da loja). Impede o erro INVALID_DELIVERY_WINDOW do backend.
    hasInvalidDeliveryWindow() {
      return (this.config.days || []).some(
        day => !!day.deliveryStartTime !== !!day.deliveryEndTime
      )
    },

    async save() {
      this.$refs.form.showErrors()
      if (!this.$refs.form.isValid) {
        this.showInvalidDataMessage()
        return
      }
      if (this.scope === 'store' && !this.selectedStoreId) {
        this.showInvalidDataMessage({ message: this.$t('Selecione uma loja') })
        return
      }
      if (this.hasInvalidDeliveryWindow()) {
        this.showInvalidDataMessage({
          message: this.$t('Em cada dia, preencha "Abre" e "Fecha" juntos, ou deixe ambos vazios para herdar o horário da loja.'),
        })
        return
      }

      try {
        this.busy = true
        if (this.scope === 'store') {
          await this.saveDeliveryConfig({ storeId: this.selectedStoreId })
        } else {
          await this.saveGeneralDeliveryConfig()
        }
        this.showSuccess({ message: this.$t('Configuração de delivery salva com sucesso') })
        await this.onScopeOrStoreChange()
      } catch (error) {
        this.showError({ error, message: this.$t('Não foi possível concluir a operação') })
      } finally {
        this.busy = false
      }
    },

    cancel() {
      this.$router.push({ name: 'delivery-config-list' })
    },
  },
}
</script>

<style></style>
